import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Icon, Spin, Table, Tag, Text, useLocale, type IconName } from "@app/ui";
import { getPlatformOverview } from "../api/platform.js";
import { BarChart } from "../dashboard/BarChart.js";
import { StatDot, StatTile } from "../dashboard/StatTile.js";
import "../dashboard/charts.css";
import { ErrorAlert } from "../errors/index.js";
import { useFormatDateTime, useT, type MessageKey } from "../i18n.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { ShowDemoToggle, useShowDemo } from "../platform/useShowDemo.js";
import type { PlatformOverview } from "../types.js";

interface AttentionItem {
  key: string;
  count: number;
  label: MessageKey;
  icon: IconName;
  to: string;
  tone: "error" | "warning" | "default";
}

/**
 * Platform-admin home: platform-wide numbers, what needs attention (each item
 * links to the filtered list that resolves it), passport views over 30 days and
 * the newest tenants. Everything here spans all organizations.
 */
export function PlatformOverviewPage() {
  const t = useT();
  const navigate = useNavigate();
  const fmt = useFormatDateTime();
  const { locale } = useLocale();
  const nf = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const { selectOrg } = useOrgs();
  const { showDemo } = useShowDemo();
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await getPlatformOverview(showDemo));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => void load(), [showDemo]);

  if (error != null) return <ErrorAlert error={error} onRetry={() => void load()} />;
  if (!data) return <Spin />;
  const x = data.totals;

  const attention: AttentionItem[] = (
    [
      {
        key: "delinquent",
        count: x.delinquentOrgs,
        label: "platform.att.delinquent",
        icon: "warning",
        to: "/platform/orgs?flag=delinquent",
        tone: "error",
      },
      {
        key: "viewLimit",
        count: x.viewLimitOrgs,
        label: "platform.att.viewLimit",
        icon: "eye",
        to: "/platform/orgs?flag=viewLimit",
        tone: "warning",
      },
      {
        key: "suspended",
        count: x.suspendedOrgs,
        label: "platform.att.suspended",
        icon: "lock",
        to: "/platform/orgs?status=suspended",
        tone: "warning",
      },
      {
        key: "noMembers",
        count: x.orgsWithoutMembers,
        label: "platform.att.orgsWithoutMembers",
        icon: "building",
        to: "/platform/orgs?flag=noMembers",
        tone: "warning",
      },
      {
        key: "noOrg",
        count: x.usersWithoutOrg,
        label: "platform.att.usersWithoutOrg",
        icon: "users",
        to: "/users?withoutOrg=1",
        tone: "default",
      },
      {
        key: "unverified",
        count: x.unverifiedUsers,
        label: "platform.att.unverified",
        icon: "mail",
        to: "/users",
        tone: "default",
      },
      {
        key: "disabled",
        count: x.disabledUsers,
        label: "platform.att.disabledUsers",
        icon: "user",
        to: "/users?status=disabled",
        tone: "default",
      },
    ] satisfies AttentionItem[]
  ).filter((a) => a.count > 0);

  return (
    <div className="dpp-dash">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Text strong>{t("platform.title")}</Text>
          <div>
            <Text small secondary>
              {showDemo
                ? t("platform.subtitleWithDemo")
                : t("platform.subtitleNoDemo", { orgs: data.demo.orgs, users: data.demo.users })}
            </Text>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ShowDemoToggle />
          <Button
            size="sm"
            icon={<Icon name="plus" size={14} />}
            onClick={() => navigate("/platform/orgs?new=1")}
          >
            {t("adminOrgs.create")}
          </Button>
          <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={() => navigate("/users?new=1")}>
            {t("adminUsers.create")}
          </Button>
        </div>
      </div>

      <div className="dpp-kpis">
        <StatTile
          index={0}
          label={t("nav.adminOrgs")}
          value={nf.format(x.orgs)}
          icon="building"
          tone="indigo"
          note={t("platform.note.activeOrgs", { n: nf.format(x.activeOrgs) })}
        />
        <StatTile
          index={1}
          label={t("nav.users")}
          value={nf.format(x.users)}
          icon="users"
          tone="violet"
          note={t("platform.note.disabledUsers", { n: nf.format(x.disabledUsers) })}
        />
        <StatTile
          index={2}
          label={t("nav.products")}
          value={nf.format(x.products)}
          icon="box"
          tone="teal"
          note={t("platform.note.views30d", { n: nf.format(x.views30d) })}
        />
        <StatTile
          index={3}
          label={t("nav.passports")}
          value={nf.format(x.passports)}
          icon="file-text"
          tone="coral"
          note={
            <>
              <StatDot tone="live" /> {t("platform.note.published", { n: nf.format(x.publishedPassports) })}{" "}
              <StatDot tone="draft" /> {t("platform.note.draft", { n: nf.format(x.draftPassports) })}
            </>
          }
        />
      </div>

      <div className="dpp-charts-2up">
        <Card title={t("platform.attention")}>
          {attention.length === 0 ? (
            <div className="flex items-center gap-2">
              <Icon name="success" size={16} />
              <Text secondary>{t("platform.allGood")}</Text>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {attention.map((a) => (
                <Button
                  key={a.key}
                  variant="subtle"
                  className="w-full justify-between"
                  onClick={() => navigate(a.to)}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon name={a.icon} size={14} />
                    {t(a.label)}
                  </span>
                  <Tag color={a.tone}>{nf.format(a.count)}</Tag>
                </Button>
              ))}
            </div>
          )}
        </Card>
        <Card title={t("platform.views")}>
          {data.viewsByDay.length === 0 ? (
            <Text secondary>{t("platform.noViews")}</Text>
          ) : (
            <BarChart
              data={data.viewsByDay.map((d) => ({ month: d.day, label: d.day.slice(5), count: d.n }))}
              title={t("platform.views")}
              formatNumber={(v) => nf.format(v)}
            />
          )}
        </Card>
      </div>

      <Card
        title={t("platform.recentOrgs")}
        extra={
          <Button size="sm" variant="subtle" onClick={() => navigate("/platform/orgs")}>
            {t("platform.seeAll")}
          </Button>
        }
      >
        <Table
          rowKey={(o) => o.id}
          empty={t("adminOrgs.empty")}
          onRowClick={(o) => {
            selectOrg(o.id);
            navigate("/org");
          }}
          columns={[
            { key: "name", title: t("adminOrgs.name"), render: (o) => o.name },
            {
              key: "status",
              title: t("common.status"),
              render: (o) => (
                <Tag color={o.status === "active" ? "success" : "warning"}>
                  {t(`adminOrgs.status.${o.status}`)}
                </Tag>
              ),
            },
            {
              key: "plan",
              title: t("org.plan"),
              render: (o) => t(`adminOrgs.plan.${o.billingPlan}`),
              hideOnMobile: true,
            },
            { key: "created", title: t("common.createdAt"), render: (o) => fmt(o.createdAt) },
          ]}
          data={data.recentOrgs}
        />
      </Card>
    </div>
  );
}
